/**
 * PublicChurchCalendar — the assembly's own upcoming services and events.
 *
 * `UpcomingCalendarSection` sits on /church/:slug and lists the next few
 * public entries. `PublicChurchCalendarPage` (/church/:slug/calendar) is the
 * full month grid. Internal entries — staff meetings, maintenance, finance
 * reviews — are filtered out on the server and never reach either.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays, Clock, MapPin, ArrowLeft, ChevronLeft, ChevronRight, X, CalendarPlus,
} from 'lucide-react';
import { isoDay } from '@/lib/localDate';
import SubscribeCalendar from '@/components/church/SubscribeCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const fetchCalendar = async (slug, params) => {
  const qs = new URLSearchParams(params).toString();
  const r = await fetch(`${API_URL}/api/public/church-calendar/${slug}?${qs}`);
  if (!r.ok) throw new Error('Calendar unavailable');
  return r.json();
};

const prettyDate = (d) => new Date(`${d}T00:00`).toLocaleDateString(undefined, {
  weekday: 'short', day: 'numeric', month: 'short',
});

const DetailCard = ({ item, dark, slug, onClose }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}
       data-testid="public-calendar-detail">
    <div onClick={(e) => e.stopPropagation()}
         className={`w-full max-w-md rounded-2xl p-6 ${dark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-800'}`}>
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-lg font-semibold">{item.title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"
                data-testid="public-calendar-detail-close"><X className="h-5 w-5" /></button>
      </div>
      <p className="mt-2 flex items-center gap-2 text-sm opacity-80">
        <Clock className="h-4 w-4" />{prettyDate(item.date)}{item.time ? ` · ${item.time}` : ' · all day'}
      </p>
      {item.location && (
        <p className="mt-1 flex items-center gap-2 text-sm opacity-80">
          <MapPin className="h-4 w-4" />{item.location}
        </p>
      )}
      {item.description && <p className="mt-3 text-sm leading-relaxed opacity-90">{item.description}</p>}
      {item.recurring && (
        <p className="mt-3 text-xs opacity-60">This gathering happens every week.</p>
      )}
      <a href={`${API_URL}/api/public/church-calendar/${slug}/calendar.ics?item_id=${encodeURIComponent(item.id)}`}
         data-testid="public-calendar-add-to-calendar"
         className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
         style={{ backgroundColor: 'var(--cp-accent, #4f46e5)' }}>
        <CalendarPlus className="h-4 w-4" /> Add to my calendar
      </a>
    </div>
  </div>
);

/** Compact "what's on" list for the church landing page. */
export const UpcomingCalendarSection = ({ slug, dark }) => {
  const [open, setOpen] = useState(null);
  const { data } = useQuery({
    queryKey: ['public-church-calendar', slug, 'upcoming'],
    queryFn: () => fetchCalendar(slug, { limit: 10 }),
    retry: false,
  });

  if (!data || (data.items || []).length === 0) return null;

  return (
    <section className={`px-6 py-16 ${dark ? 'bg-slate-950' : 'bg-white'}`}
             data-testid="public-calendar-section">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em]"
               style={{ color: 'var(--cp-accent)' }}>What's on</p>
            <h2 className={`mt-1 text-2xl font-bold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
              Upcoming at {data.church?.name}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <SubscribeCalendar slug={slug} dark={dark} />
            <Link to={`/church/${slug}/calendar`} data-testid="public-calendar-see-all"
                  className="text-sm font-medium hover:underline" style={{ color: 'var(--cp-accent)' }}>
              See the full calendar →
            </Link>
          </div>
        </div>
        <ul className={`divide-y ${dark ? 'divide-slate-800' : 'divide-slate-100'}`}>
          {data.items.map((i) => (
            <li key={i.id}>
              <button onClick={() => setOpen(i)} data-testid={`public-calendar-item-${i.id}`}
                      className="flex w-full items-center gap-4 py-4 text-left">
                <span className={`w-16 shrink-0 rounded-lg px-2 py-1.5 text-center ${
                  dark ? 'bg-slate-900' : 'bg-slate-50'}`}>
                  <span className={`block text-[10px] uppercase ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {MONTHS[Number(i.date.slice(5, 7)) - 1].slice(0, 3)}
                  </span>
                  <span className={`block text-lg font-bold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                    {Number(i.date.slice(8))}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate font-medium ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
                    {i.title}
                  </span>
                  <span className={`block truncate text-xs ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {i.time || 'All day'}{i.location ? ` · ${i.location}` : ''}
                    {i.description ? ` · ${i.description.slice(0, 70)}` : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {open && <DetailCard item={open} dark={dark} slug={slug} onClose={() => setOpen(null)} />}
    </section>
  );
};

/** Full public month calendar at /church/:slug/calendar */
const PublicChurchCalendarPage = () => {
  const { slug } = useParams();
  const [anchor, setAnchor] = useState(new Date());
  const [open, setOpen] = useState(null);

  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  // The grid cells below are built from local parts and were always right,
  // but the window asked of the server went through `toISOString()` — so east
  // of Greenwich it requested the previous month's last day and never asked
  // for this month's, and the last day of every month appeared empty.
  const start = isoDay(first);
  const end = isoDay(last);

  const { data, error } = useQuery({
    // `end` belongs in the key: without it, paging to a month of a different
    // length served the previous month's response from cache.
    queryKey: ['public-church-calendar', slug, start, end],
    queryFn: () => fetchCalendar(slug, { start, end }),
    retry: false,
  });

  const cells = [];
  const lead = (first.getDay() + 6) % 7;
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= last.getDate(); d += 1) {
    cells.push(`${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (cells.length % 7) cells.push(null);
  const days = data?.days || {};
  const todayIso = isoDay();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-calendar-page">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-5">
          <div className="flex items-center gap-3">
            <Link to={`/church/${slug}`} className="text-slate-400 hover:text-slate-700"
                  data-testid="public-calendar-back"><ArrowLeft className="h-5 w-5" /></Link>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <CalendarDays className="h-5 w-5 text-indigo-600" /> What's on
              </h1>
              <p className="text-xs text-slate-500" data-testid="public-calendar-church">
                {data?.church?.name || slug}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SubscribeCalendar slug={slug} />
            <a href={`${API_URL}/api/public/church-calendar/${slug}/calendar.ics`}
               data-testid="public-calendar-subscribe"
               className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <CalendarPlus className="h-3.5 w-3.5" /> Add all to my calendar
            </a>
            <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
                    className="grid h-8 w-8 place-items-center rounded-md border border-slate-200 hover:bg-slate-50"
                    data-testid="public-calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-0 sm:min-w-[140px] text-center text-sm font-semibold text-slate-800"
                  data-testid="public-calendar-month">
              {MONTHS[anchor.getMonth()]} {anchor.getFullYear()}
            </span>
            <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
                    className="grid h-8 w-8 place-items-center rounded-md border border-slate-200 hover:bg-slate-50"
                    data-testid="public-calendar-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {error ? (
          <p className="py-20 text-center text-sm text-slate-500" data-testid="public-calendar-error">
            This church has not published a calendar.
          </p>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-1 grid grid-cols-7 gap-1">
                {WEEKDAYS.map((d) => (
                  <p key={d} className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{d}</p>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((day, i) => (
                  <div key={day || `pad-${i}`} data-testid={day ? `public-calendar-day-${day}` : undefined}
                       className={`min-h-[92px] rounded-lg border p-1.5 ${
                         !day ? 'border-transparent' : day === todayIso ? 'border-slate-900' : 'border-slate-100'}`}>
                    {day && (
                      <>
                        <p className={`px-1 text-[11px] ${day === todayIso ? 'font-bold text-slate-900' : 'text-slate-400'}`}>
                          {Number(day.slice(8))}
                        </p>
                        <div className="space-y-1">
                          {(days[day] || []).map((i2) => (
                            <button key={i2.id} onClick={() => setOpen(i2)}
                                    data-testid={`public-calendar-cell-${i2.id}`}
                                    className="w-full truncate rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-left text-[10px] text-indigo-700">
                              {i2.time && <span className="mr-1 font-semibold tabular-nums">{i2.time}</span>}
                              {i2.title}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {(data?.items || []).length === 0 && (
              <p className="py-10 text-center text-sm text-slate-500" data-testid="public-calendar-empty">
                Nothing published for {MONTHS[anchor.getMonth()]} yet — check back soon.
              </p>
            )}
          </>
        )}
      </main>
      {open && <DetailCard item={open} dark={false} slug={slug} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default PublicChurchCalendarPage;
